/**
 * On-screen review & annotation ("Review Mode") — shared types.
 *
 * A "note" is one piece of feedback pinned to a screen: what was drawn, what
 * was said, and enough anchoring to find the spot again after the UI changes.
 *
 * Ported from the YouPropCloud reference implementation
 * (docs brief: SCREEN-REVIEW-PORT-BRIEF.md) with these deltas:
 *   - reviewer identity is the logged-in user (personal logins here), not a
 *     free-text name;
 *   - a `redact` shape exists because PropCloudAI screens show real customer
 *     CRM data — redacted regions are ALSO burned into the stored JPEG;
 *   - notes capture userAgent / appVersion / recent console errors;
 *   - a note can be promoted into a supportTickets doc (`promotedTicketId`).
 *
 * Kept free of Firebase imports except `Timestamp` so the drawing model stays
 * usable in pure utils and tests.
 */
import type { Timestamp } from 'firebase/firestore';

// ---------------------------------------------------------------------------
// Drawing model — vector, not raster.
//
// All coordinates are 0–1 fractions of the annotated viewport, so shapes
// re-render correctly at any window size and can be replayed over the stored
// screenshot at whatever size it displays.
// ---------------------------------------------------------------------------

export interface PinShape {
  kind: 'pin';
  x: number;
  y: number;
}
export interface ArrowShape {
  kind: 'arrow';
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}
export interface RectShape {
  kind: 'rect';
  x: number;
  y: number;
  w: number;
  h: number;
}
export interface FreehandShape {
  kind: 'freehand';
  /** Polyline as [x, y] pairs, 0–1. */
  points: [number, number][];
}
export interface TextShape {
  kind: 'text';
  x: number;
  y: number;
  text: string;
}
/**
 * Redaction box. Rendered as a solid fill on screen AND burned into the
 * stored screenshot's pixels at capture time — removing the vector layer must
 * never reveal what was underneath.
 */
export interface RedactShape {
  kind: 'redact';
  x: number;
  y: number;
  w: number;
  h: number;
}

export type ReviewShape =
  | PinShape
  | ArrowShape
  | RectShape
  | FreehandShape
  | TextShape
  | RedactShape;

export type ReviewTool = ReviewShape['kind'];

// ---------------------------------------------------------------------------
// Anchoring — three layers, degrading gracefully.
// ---------------------------------------------------------------------------

export interface ReviewAnchor {
  /** Pathname with volatile ids normalised: `/deals/abc123` → `/deals/:id`. */
  routePattern: string;
  /** The raw pathname at capture time — exact repro. */
  route: string;
  /** Permanent nav/menu key (e.g. `hq.salesTools.callCentreBook`) — null when unmapped. */
  navKey: string | null;
  /** Human breadcrumb, e.g. "HQ → Sales Tools → Call Centre". Falls back to the route. */
  navLabel: string;
  /** Focal point of the note, 0–1 of the viewport. */
  xPct: number;
  yPct: number;
  /** Best-effort description of what was under the focal point. */
  elementText: string | null;
  elementPath: string | null;
}

// ---------------------------------------------------------------------------
// Notes + sessions
// ---------------------------------------------------------------------------

export const REVIEW_CATEGORIES = ['bug', 'change', 'idea', 'question'] as const;
export type ReviewCategory = (typeof REVIEW_CATEGORIES)[number];

export const REVIEW_SEVERITIES = ['low', 'medium', 'high'] as const;
export type ReviewSeverity = (typeof REVIEW_SEVERITIES)[number];

/** Triage lifecycle, driven from the Support Hub. */
export const REVIEW_NOTE_STATUSES = ['open', 'accepted', 'done', 'rejected'] as const;
export type ReviewNoteStatus = (typeof REVIEW_NOTE_STATUSES)[number];

export interface ScreenReviewNote {
  id: string;
  sessionId: string;
  /** users-collection document id of the reviewer. */
  userId: string;
  /** Firebase Auth UID at capture time (dual-ID system). */
  userAuthUid: string;
  userName: string;
  userEmail: string;
  userRole: string;
  body: string;
  category: ReviewCategory;
  severity: ReviewSeverity;
  status: ReviewNoteStatus;
  shapes: ReviewShape[];
  anchor: ReviewAnchor;
  /** Viewport at capture time — lets shapes replay at the right aspect. */
  viewportW: number;
  viewportH: number;
  screenshotPath: string | null;
  screenshotUrl: string | null;
  /** Diagnostics captured with the note — they cost nothing and answer "what browser?". */
  userAgent: string;
  appVersion: string;
  /** Most recent console.error messages at save time (ring buffer, newest last). */
  consoleErrors: string[];
  /** Triage extras: who owns the fix, and where the work lives. */
  assignedTo: string | null;
  taskRef: string | null;
  /** Set when the note is promoted into a supportTickets doc. */
  promotedTicketId: string | null;
  /** AI-generated problem summary + implementation brief (super-admin action). */
  aiAnalysis: ReviewNoteAiAnalysis | null;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
}

export interface ReviewNoteAiAnalysis {
  /** Plain-language problem summary for triage. */
  summary: string;
  /** Structured implementation brief, ready to hand to Claude Code. */
  brief: string;
  model: string;
  generatedAt: Timestamp | null;
  generatedByName: string;
}

export type ReviewSessionStatus = 'open' | 'closed';

export interface ScreenReviewSession {
  id: string;
  title: string;
  status: ReviewSessionStatus;
  createdAt: Timestamp | null;
  closedAt: Timestamp | null;
  /** Optional AI-generated summary of the session (deferred phase). */
  aiSummary: string | null;
  aiSummaryGeneratedAt: Timestamp | null;
}
